import type { FunctionComponent } from 'react';
import { Fragment, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { config } from '../../../../config/config';
import { isInApp } from '../../../main/util/is-app';
import type { IFieldError } from '../../../registration/registration.interface';
import { createClassName, createStyleHelper } from '../../../shared/utils/class-names';
import { useContextNavigate } from '../../hooks/use-context-navigate';
import { slugify } from '../../utils/strings';
import { Button, ButtonSize } from '../button/button';
import { FormattedMessage } from '../formatted-message/formatted-message';

import styles from './message-state.module.scss';

const style = createStyleHelper(styles, 'message-state');

export interface IMessageStateProps {
    className?: string;
    title?: string;
    message?: string;
    actionText?: string;
    actionHref?: string;
    errors?: IFieldError[];
    isLoading?: boolean;
}

export const MessageState: FunctionComponent<IMessageStateProps> = ({
    className,
    title,
    message,
    actionHref,
    actionText,
    errors,
    isLoading = true,
}) => {
    const intl = useIntl();
    const navigate = useContextNavigate();
    const isNativeApp = isInApp();
    const goToAction = useCallback(() => {
        if (isNativeApp) {
            try {
                window.parent.postMessage(
                    { message: 'pam_request_close_mobile_app_webview' },
                    config.SPORT_ENDPOINT,
                );
            } catch (error) {
                console.error('An error occurred while executing postMessage:', error);
            }
            return;
        }
        if (!actionHref) {
            return;
        }

        navigate(actionHref);
    }, [actionHref, navigate]);

    return (
        <div className={createClassName([className, style()])}>
            {!isLoading && (
                <div>
                    <div className={style('heading')}>
                        {intl.formatMessage({
                            id: title,
                            defaultMessage: title,
                        })}
                    </div>
                    <div className={style('message')}>
                        <div>
                            {intl.formatMessage({
                                id: message,
                                defaultMessage: message,
                            })}
                        </div>
                    </div>
                    <div className={style('error')}>
                        {errors &&
                            errors?.length > 0 &&
                            errors.map((error, index) => (
                                <Fragment key={index}>
                                    {error.kind.map((errorKind) => (
                                        <p key={errorKind}>
                                            <FormattedMessage
                                                id={`registration.error.${slugify(errorKind)}`}
                                                defaultMessage={
                                                    '{titleCaseField} has a {kindLower} error.'
                                                }
                                                values={{
                                                    kind: errorKind,
                                                    kindLower: errorKind.toLowerCase(),
                                                    field: error.fieldName,
                                                    titleCaseField:
                                                        error.fieldName.charAt(0).toUpperCase() +
                                                        error.fieldName.slice(1),
                                                    pattern: error.pattern,
                                                }}
                                            />
                                        </p>
                                    ))}
                                </Fragment>
                            ))}
                    </div>
                    {actionHref && !errors ? (
                        <div className={style('actions')}>
                            <Button size={ButtonSize.LARGE} onClick={goToAction}>
                                {intl.formatMessage({
                                    id: actionText,
                                })}
                            </Button>
                        </div>
                    ) : null}
                </div>
            )}
        </div>
    );
};
